/* bios-cursor.js — global crosshair cursor + floating XY coords label
   + accent-button click glitch.
   Loaded from theme.liquid so it runs on every page (main, cart, etc.).
   Lerp factor 0.40 keeps motion smooth without noticeable lag.
   z-indexes are above the portal overlay (9999) so coords show on intro/boot. */

/* ── Accent-button click glitch ────────────────────────────────────────────
   Lives in its OWN IIFE outside the cursor block so it fires on both
   desktop and touch. On touch the cursor IIFE bails out entirely; the
   click glitch is pure DOM/style work with no dependency on cursorEl,
   so we lift it out to keep tap feedback parity with desktop. */
(function biosAccentClickGlitch() {
  'use strict';

  var ACCENT_CLICK_SEL = [
    /* Portal nav */
    '.bios-portal-nav-btn',
    '.bios-portal-nav-link',
    '.bios-portal-choice',
    '.bios-portal-choice-label',
    /* PDP + cart + search + account secondary surfaces — previously dead
       on click. Each of these is a primary action button on its page. */
    '.bios-prod-pg-add',
    '.bios-acct-pg-submit',
    '.bios-cart-pg-checkout',
    '.bios-cart-pg-back',
    '.bios-coll-pg-page-link',
    '.bios-srch-submit',
    '.bios-ov-cart-btn',
    '.bios-ov-close',
    '.bios-sell-submit',
    '.bios-offer-submit',
    '.bios-cart-close-btn',
    '.bios-doc-back',
    '.bios-doc-page-link',
    /* Shopify customer-privacy banner (currently hidden but kept in case
       a future template surfaces it) */
    '.shopify-pc__banner a',
    '.shopify-pc__banner button',
    '.cb-banner a',
    '.cb-banner button',
    '[id^="shopify-pc"] a',
    '[id^="shopify-pc"] button',
    /* Opt-in marker for any future button that should glitch on click */
    '[data-accent-click]'
  ].join(',');

  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest && e.target.closest(ACCENT_CLICK_SEL);
    if (!el || el._accentGlitching) return;
    el._accentGlitching = true;

    /* Suspend ambient flicker so it doesn't override the glitch */
    var hadFlicker = el.classList.contains('is-flickering');
    if (hadFlicker) el.classList.remove('is-flickering');

    /* Inline !important wins over every stylesheet rule regardless of
       specificity — including .bios-portal-nav-btn.is-flickering's
       higher-specificity animation. */
    el.style.setProperty('animation', 'bios-accent-click-glitch 450ms step-end', 'important');
    el.style.setProperty('color',     'var(--bios-accent)',                       'important');
    el.style.setProperty('text-shadow', 'none',                                   'important');

    setTimeout(function () {
      el.style.removeProperty('animation');
      el.style.removeProperty('color');
      el.style.removeProperty('text-shadow');
      el._accentGlitching = false;
    }, 470);
  }, true /* capture phase so it runs before any stopPropagation */);
})();

/* ── Brand-name idle Japanese glitch ────────────────────────────────────────
   Same effect as the portal-landing ASCII wordmark: occasionally swap 1-2
   characters in `.bios-brand-name` with chars from a Japanese/symbol
   glitch set, then restore. Runs globally so the header wordmark
   maintains a slow "system disturbance" pulse on every page. */
(function biosBrandGlitch() {
  'use strict';
  function init() {
    var brand = document.querySelector('.bios-brand-name');
    if (!brand) return;
    var ORIG = brand.textContent;
    if (!ORIG || ORIG.length < 2) return;
    var GLITCH_SET = '01#@░▒▓█&%?!▄▀ア'.split('');
    function glitch() {
      /* Defensive: if some other code (e.g., portal-home click) has
         changed or removed the brand, bail until next interval. */
      if (!brand.isConnected || brand.textContent.length !== ORIG.length) {
        schedule(); return;
      }
      var chars = ORIG.split('');
      var hits = 1 + Math.floor(Math.random() * 2); /* 1-2 chars */
      for (var i = 0; i < hits; i++) {
        var idx = Math.floor(Math.random() * chars.length);
        chars[idx] = GLITCH_SET[Math.floor(Math.random() * GLITCH_SET.length)];
      }
      brand.textContent = chars.join('');
      setTimeout(function () {
        if (brand.isConnected) brand.textContent = ORIG;
        schedule();
      }, 70 + Math.random() * 110);
    }
    function schedule() {
      setTimeout(glitch, 2200 + Math.random() * 4000);
    }
    schedule();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function biosGlobalCursor() {
  'use strict';

  var isTouchDevice = ('ontouchstart' in window) ||
    navigator.maxTouchPoints > 1 ||
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (isTouchDevice) return;

  /* ── Find or create cursor element ── */
  var cursorEl = document.getElementById('bios-cursor');
  if (!cursorEl) {
    cursorEl = document.createElement('div');
    cursorEl.id = 'bios-cursor';
    cursorEl.className = 'bios-cursor';
    cursorEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cursorEl);
  }

  /* ── Find existing coords label (do NOT auto-create) ─────────────────
     The coords readout lives inline inside .bios-manifest on the catalog
     ([EXPERIENCE]) page only — that single source of truth is what gets
     ticked here. On secondary pages (sketches / lab / blog / info / faq
     / cart / pdp) the element doesn't exist and we skip coord updates
     entirely. The old behaviour was to inject a floating coord readout
     in the bottom-right corner of every page; that duplicated the
     manifest-bar variant on the homepage and looked stranded on doc
     pages. */
  var coordsEl = document.getElementById('bios-mouse-coords');

  /* ── Lerp state ── */
  var LERP    = 0.40;  /* 0=frozen · 1=instant · 0.40 = snappy with slight smoothing */
  var _mxT    = -9999, _myT = -9999;
  var _mxC    = -9999, _myC = -9999;
  var _ready  = false;

  /* ── Edge / text-input detection ── */
  var EDGE = 38;
  function isTextCtx(el) {
    if (!el) return false;
    var t = el.tagName;
    if (t === 'INPUT' || t === 'TEXTAREA') return true;
    try { return window.getComputedStyle(el).cursor === 'text'; } catch (_) { return false; }
  }

  /* ── Show / hide coords ── */
  /* Null-guards everywhere: on pages without a manifest XY, coordsEl is
     null and these become no-ops. */
  var _cVis = false, _cTimer = null;
  function showCoords() {
    if (_cVis || !coordsEl) return;
    _cVis = true;
    clearTimeout(_cTimer);
    coordsEl.classList.remove('coords-out');
    void coordsEl.offsetWidth;
    coordsEl.classList.add('coords-in');
  }
  function hideCoords() {
    if (!_cVis || !coordsEl) return;
    _cVis = false;
    coordsEl.classList.remove('coords-in');
    void coordsEl.offsetWidth;
    coordsEl.classList.add('coords-out');
    clearTimeout(_cTimer);
    _cTimer = setTimeout(function() {
      if (!coordsEl) return;
      coordsEl.classList.remove('coords-out');
      coordsEl.style.opacity = '0';
    }, 220);
  }

  /* ── RAF lerp loop — cursor + coords both follow lerped position ── */
  (function _raf() {
    if (_mxT > -999) {
      _mxC += (_mxT - _mxC) * LERP;
      _myC += (_myT - _myC) * LERP;

      cursorEl.style.left = _mxC.toFixed(1) + 'px';
      cursorEl.style.top  = _myC.toFixed(1) + 'px';

      if (_ready && _cVis && coordsEl) {
        var cx = Math.round(_mxC), cy = Math.round(_myC);
        /* Only update text content — position is governed by CSS.
           Now the only XY readout lives in the .bios-manifest bar
           (.bios-manifest-xy variant) on the catalog page. */
        coordsEl.textContent =
          'X:' + String(cx).padStart(4, '0') +
          '  Y:' + String(cy).padStart(4, '0');
      }
    }
    requestAnimationFrame(_raf);
  })();

  /* ── Mouse handlers ── */
  document.addEventListener('mousemove', function(e) {
    var x = e.clientX, y = e.clientY;
    _mxT = x; _myT = y;
    _ready = true;
    var vw = window.innerWidth, vh = window.innerHeight;
    var nearEdge = x < EDGE || y < EDGE || x > vw - EDGE || y > vh - EDGE;
    if (nearEdge || isTextCtx(e.target)) {
      hideCoords();
    } else {
      showCoords();
    }
  }, { passive: true });

  document.addEventListener('mouseleave', hideCoords);

  /* ── Cursor colour invert based on the hovered element's actual colour ─
     Cursor is amber by default. When it lands on anything that's *also*
     amber (default-amber elements + buttons that flip to amber on hover),
     it would visually disappear into the element. Detect this in real-
     time by reading getComputedStyle(target).color at mouseover. The
     :hover styles are already active by the time the event fires, so we
     see the actual displayed colour, including hover transitions.

     This is more robust than maintaining a selector allowlist:
       · default-amber elements: cursor → white                  ✓
       · default-anything element that turns amber on hover:
         cursor → white once you cross into it                   ✓
       · default-amber element (e.g. portal nav) that turns
         WHITE on hover: at mouseover, computed colour is white,
         not amber — cursor stays amber, so contrast is preserved ✓ */
  function isAmberish(r, g, b) {
    /* Warm orange/amber: R > G > B with R notably high. Tuned around
       the accent #d97757 = rgb(217, 119, 87). Allows variant shades
       so :hover transitions and accent-dim variations also match. */
    return r > 180 && (r - b) > 60 && (r - g) > 50;
  }
  document.addEventListener('mouseover', function (e) {
    var t = e.target;
    if (!t || t === cursorEl || !(t instanceof Element)) return;
    var c = window.getComputedStyle(t).color;
    var m = c && c.match(/(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)/);
    if (!m) { cursorEl.classList.remove('is-over-accent'); return; }
    var hit = isAmberish(+m[1], +m[2], +m[3]);
    cursorEl.classList.toggle('is-over-accent', hit);
  }, { passive: true });

  /* Accent-button click glitch — handled by the lifted `biosAccentClickGlitch`
     IIFE at the top of this file (fires on both desktop and touch). The
     duplicate handler that used to live here has been removed so the
     glitch animation isn't queued twice on desktop clicks. */
})();

/* ── Portal-home navigation ──────────────────────────────────────────────────
   Any element marked with [data-portal-home] (the INNOCENCES wordmark on the
   store page, the BIOS uplink wordmark, the [RETURN] back-link on doc pages)
   should land the user back on the portal welcome screen — not the BIOS or
   the typical-store auto-redirect.

   Without this, /pages/store's logo links to "/" but the bios-portal-onboarding
   redirect immediately bounces the user back to /pages/store because
   bios_choice === 'store' is in sessionStorage. Same trap on doc-page [RETURN].

   Clearing both session keys forces the portal to render. Runs on every page
   because bios-cursor.js is loaded from theme.liquid. */
(function biosPortalHome() {
  'use strict';
  document.addEventListener('click', function(e) {
    var el = e.target.closest('[data-portal-home]');
    if (!el) return;
    /* Modified-click / middle-click → open in new tab, skip glitch. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;

    /* ALWAYS force a return to the welcome screen:
         1. Clear both sessionStorage keys so the portal renders fresh.
         2. preventDefault so the browser doesn't follow href before
            the glitch animation completes.
         3. Route through window.biosPageGlitch() for the same step-end
            corruption flash every internal nav uses, then either
            reload (if already on /) or navigate to /. */
    try {
      sessionStorage.removeItem('bios_choice');
      sessionStorage.removeItem('bios_portal_choice');
    } catch (_) {}
    e.preventDefault();

    var goHome = function () {
      if (location.pathname === '/' || location.pathname === '') {
        location.reload();
      } else {
        location.href = '/';
      }
    };

    if (window.biosPageGlitch) {
      try { if (window.BiosAudio) window.BiosAudio.pageStaticBurst(); } catch (_) {}
      window.biosPageGlitch(goHome);
    } else {
      goHome();
    }
  }, true);
})();
